import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { bookingAPI } from '../../api';
import { Badge, Spinner, EmptyState, Pagination } from '../../components/common/Misc';
import Button from '../../components/common/Button';
import styles from './AdminDashboard.module.css';

const STATUSES = ['', 'pending', 'confirmed', 'active', 'completed', 'cancelled'];
const NEXT_STATUS = { pending: 'confirmed', confirmed: 'active', active: 'completed' };

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [page,     setPage]     = useState(1);
  const [pages,    setPages]    = useState(1);
  const [filter,   setFilter]   = useState('');

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (filter) params.status = filter;
      const res = await bookingAPI.getAll(params);
      setBookings(res.data.data);
      setPages(res.data.pages);
    } finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const advance = async (id, status) => {
    try {
      await bookingAPI.updateStatus(id, { status });
      toast.success(`Booking marked as ${status}`);
      fetchBookings();
    } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking permanently?')) return;
    try { await bookingAPI.delete(id); toast.success('Deleted'); fetchBookings(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>All bookings</h2>
        <div style={{ display:'flex', gap:'.4rem', flexWrap:'wrap' }}>
          {STATUSES.map(s => (
            <button
              key={s}
              onClick={() => { setFilter(s); setPage(1); }}
              style={{
                padding: '.3rem .75rem', borderRadius: '100px', border: '1.5px solid',
                fontSize: '.78rem', fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize',
                borderColor: filter === s ? 'var(--color-primary)' : 'var(--color-border)',
                background:  filter === s ? 'var(--color-primary)' : 'none',
                color:       filter === s ? '#fff' : 'var(--color-text-secondary)',
              }}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : bookings.length === 0
        ? <EmptyState icon="📋" title="No bookings" />
        : (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr><th>Bike</th><th>Staff</th><th>Start</th><th>End</th><th>Amount</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b._id}>
                    <td><strong>{b.bike_id?.name || ''}</strong></td>
                    <td>{b.user_id?.name || ''}<br /><small style={{ color:'var(--color-text-muted)' }}>{b.user_id?.email}</small></td>
                    <td>{format(new Date(b.start_time), 'dd MMM, HH:mm')}</td>
                    <td>{format(new Date(b.end_time),   'dd MMM, HH:mm')}</td>
                    <td>₹{b.final_amount?.toFixed(2)}</td>
                    <td><Badge label={b.status} status={b.status} /></td>
                    <td>
                      <div className={styles.actionBtns}>
                        {NEXT_STATUS[b.status] && (
                          <Button size="sm" variant="success" onClick={() => advance(b._id, NEXT_STATUS[b.status])}>
                            → {NEXT_STATUS[b.status]}
                          </Button>
                        )}
                        {['pending','confirmed'].includes(b.status) && (
                          <Button size="sm" variant="danger" onClick={() => advance(b._id, 'cancelled')}>Cancel</Button>
                        )}
                        <Button size="sm" variant="secondary" onClick={() => handleDelete(b._id)}>Del</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
      <Pagination page={page} pages={pages} onChange={setPage} />
    </div>
  );
};

export default AdminBookings;
